import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Table,
  Alert,
  Spinner,
  Button,
  Modal,
  Form,
  Nav,
  Tab,
} from "react-bootstrap";
import { CSVLink } from "react-csv";
import { useAuth } from "../contexts/AuthContext";
import { fires } from "../firebase";
import { useParams, useNavigate, Link } from "react-router-dom";

export default function OrderDetails() {
  const { currentUser } = useAuth();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const [user, setUser] = useState(null);
  const [event, setEvent] = useState(null);
  const [orderTickets, setOrderTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");



  useEffect(() => {
    if (orderId) {
      loadOrderData();
    }
  }, [orderId]);

  const loadOrderData = async () => {
    setLoading(true);
    try {
      // Load order information
      const orderSnapshot = await fires
        .collection("orders")
        .where("uniqueId", "==", orderId)
        .get();

      if (orderSnapshot.empty) {
        setError("Order not found");
        setLoading(false);
        return;
      }

      const orderDoc = orderSnapshot.docs[0];
      const orderData = orderDoc.data();
      const normalizedOrder = {
        id: orderDoc.id,
        ...orderData,
        createdAt: orderData.timestamp?.toDate?.() || new Date(),
      };
      
      setOrder(normalizedOrder);

      // Load user information if we have userEmail
      if (orderData.userEmail) {
        try {
          const userSnapshot = await fires
            .collection("users")
            .where("email", "==", orderData.userEmail)
            .get();
          
          if (!userSnapshot.empty) {
            const userDoc = userSnapshot.docs[0];
            const userData = userDoc.data();
            setUser({
              id: userDoc.id,
              ...userData,
              createdAt: userData.createdAt?.toDate?.() || new Date(),
              lastLogin: userData.lastLogin?.toDate?.() || null,
            });
          }
        } catch (err) {
          console.error("Error loading user data:", err);
        }
      }

      // Load event information if we have eventId
      if (orderData.eventId) {
        try {
          const eventDoc = await fires
            .collection("events")
            .doc(orderData.eventId)
            .get();
          
          if (eventDoc.exists) {
            const eventData = eventDoc.data();
            setEvent({
              id: eventDoc.id,
              ...eventData,
              createdAt: eventData.createdAt?.toDate?.() || new Date(),
            });
          }
        } catch (err) {
          console.error("Error loading event data:", err);
        }
      }

      await loadOrderTickets(normalizedOrder);

    } catch (err) {
      setError("Failed to load order data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTicketIdsFromOrder = (orderData) => {
    const ids = new Set();

    if (Array.isArray(orderData.ticketIds)) {
      orderData.ticketIds.forEach((ticketId) => {
        if (ticketId) ids.add(String(ticketId));
      });
    }

    if (Array.isArray(orderData.tickets)) {
      orderData.tickets.forEach((ticket) => {
        if (typeof ticket === "string") {
          ids.add(ticket);
        } else if (ticket?.id) {
          ids.add(String(ticket.id));
        } else if (ticket?.ticketId) {
          ids.add(String(ticket.ticketId));
        }
      });
    }

    return Array.from(ids);
  };

  const normalizeTicket = (ticket, fallbackId = "") => ({
    id: ticket.id || ticket.ticketId || fallbackId,
    ticketType: ticket.ticketType || ticket.priceDescription || ticket.ticketName || ticket.type || "N/A",
    firstName: ticket.firstName || ticket.userFirstName || "",
    lastName: ticket.lastName || ticket.userLastName || "",
    name: ticket.name || ticket.userName || "",
    email: ticket.email || ticket.userEmail || "",
    seat: ticket.seat || ticket.seatId || "",
    status: ticket.status || "",
    checkin: !!ticket.checkin,
  });

  const uniqueTickets = (tickets) => {
    const seen = new Set();
    return tickets.filter((ticket) => {
      const key = ticket.id || `${ticket.ticketType}-${ticket.seat}-${ticket.email}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const loadOrderTickets = async (orderData) => {
    if (!orderData?.eventId) {
      setOrderTickets([]);
      return;
    }

    setLoadingTickets(true);
    try {
      const ticketsRef = fires.collection("events").doc(orderData.eventId).collection("tickets");
      const ticketIds = getTicketIdsFromOrder(orderData);
      const tickets = [];

      if (Array.isArray(orderData.tickets)) {
        orderData.tickets.forEach((ticket) => {
          if (ticket && typeof ticket === "object") {
            tickets.push(normalizeTicket(ticket));
          }
        });
      }

      if (ticketIds.length > 0) {
        const ticketDocs = await Promise.all(ticketIds.map((ticketId) => ticketsRef.doc(ticketId).get()));
        ticketDocs.forEach((ticketDoc, index) => {
          if (ticketDoc.exists) {
            tickets.push(normalizeTicket({ id: ticketDoc.id, ...ticketDoc.data() }, ticketIds[index]));
          } else {
            tickets.push(normalizeTicket({ id: ticketIds[index], ticketId: ticketIds[index] }, ticketIds[index]));
          }
        });
      } else {
        const orderKeys = [orderData.id, orderData.uniqueId].filter(Boolean);
        const querySnapshots = await Promise.all(
          orderKeys.map((orderKey) => ticketsRef.where("orderId", "==", orderKey).get())
        );

        querySnapshots.forEach((snapshot) => {
          snapshot.docs.forEach((ticketDoc) => {
            tickets.push(normalizeTicket({ id: ticketDoc.id, ...ticketDoc.data() }, ticketDoc.id));
          });
        });
      }

      setOrderTickets(uniqueTickets(tickets));
    } catch (err) {
      console.error("Error loading order tickets:", err);
      setOrderTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };



  const getStatusBadge = (status) => {
    const variants = {
      completed: "success",
      pending: "warning",
      cancelled: "danger",
      refunded: "secondary",
      processing: "info",
    };
    return <Badge bg={variants[status] || "secondary"}>{status || "pending"}</Badge>;
  };

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return "Unknown";
    
    const ua = userAgent.toLowerCase();
    let device = "Desktop";
    let browser = "Unknown";
    let os = "Unknown";
    
    // Detect device type
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone') || ua.includes('ipad')) {
      device = "Mobile";
    } else if (ua.includes('tablet') || ua.includes('ipad')) {
      device = "Tablet";
    }
    
    // Detect browser
    if (ua.includes('chrome')) browser = "Chrome";
    else if (ua.includes('firefox')) browser = "Firefox";
    else if (ua.includes('safari') && !ua.includes('chrome')) browser = "Safari";
    else if (ua.includes('edge')) browser = "Edge";
    else if (ua.includes('opera')) browser = "Opera";
    
    // Detect OS
    if (ua.includes('windows')) os = "Windows";
    else if (ua.includes('mac os')) os = "macOS";
    else if (ua.includes('iphone') || ua.includes('ipad')) os = "iOS";
    else if (ua.includes('android')) os = "Android";
    else if (ua.includes('linux')) os = "Linux";
    
    return `${device} - ${browser} on ${os}`;
  };

  const getTicketTypeLabel = (ticketType) => {
    if (!ticketType) return "N/A";
    const priceInfo = event?.prices?.[ticketType];
    return priceInfo?.priceDescription || priceInfo?.name || ticketType;
  };

  const getTicketHolderName = (ticket) => {
    const fullName = `${ticket.firstName || ""} ${ticket.lastName || ""}`.trim();
    return fullName || ticket.name || order?.userName || "N/A";
  };

  const exportOrderData = () => {
    if (!order) return [];
    
    return [{
      "Order ID": order.uniqueId || "N/A",
      "Event": order.title || "N/A",
      "Amount": order.amount || 0,
      "Status": order.status || "N/A",
      "User Email": order.userEmail || "N/A",
      "Event ID": order.eventId || "N/A",
      "Order Date": order.createdAt?.toLocaleString() || "N/A",
      "View Source": typeof order.viewData === 'object' && order.viewData 
        ? `${order.viewData.city || 'Unknown'}, ${order.viewData.region || 'Unknown'}`
        : order.viewData || "Unknown",
      "Campaign ID": typeof order.viewData === 'object' && order.viewData 
        ? order.viewData.campaignId || "N/A"
        : "N/A",
      "Country": typeof order.viewData === 'object' && order.viewData 
        ? order.viewData.country || "N/A"
        : "N/A",
      "Device": typeof order.viewData === 'object' && order.viewData 
        ? parseUserAgent(order.viewData.fromUA)
        : "N/A",
    }];
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          {error}
          <Button variant="outline-danger" className="ms-3" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container className="py-4">
        <Alert variant="warning">
          Order not found
          <Button variant="outline-warning" className="ms-3" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Card className="mb-4" style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        border: "none",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
      }}>
        <Card.Body className="text-white">
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="me-3"
                >
                  <i className="fas fa-arrow-left"></i>
                </Button>
                <div>
                  <h2 className="mb-0">
                    <i className="fas fa-receipt me-2"></i>
                    Order Details
                  </h2>
                  <p className="mb-0 opacity-75">Order ID: {order.uniqueId}</p>
                </div>
              </div>
            </Col>
            <Col xs="auto">
              <CSVLink
                data={exportOrderData()}
                filename={`order-${order.uniqueId}.csv`}
                className="btn btn-light"
              >
                <i className="fas fa-download me-1"></i>
                Export CSV
              </CSVLink>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Order Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white"
          }}>
            <Card.Body>
              <h3>${order.amount || 0}</h3>
              <p className="mb-0">Order Amount</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
            color: "white"
          }}>
            <Card.Body>
              <h3>{getStatusBadge(order.status)}</h3>
              <p className="mb-0">Order Status</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            color: "white"
          }}>
            <Card.Body>
              <h3>{order.createdAt?.toLocaleDateString() || "N/A"}</h3>
              <p className="mb-0">Order Date</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center" style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            color: "white"
          }}>
            <Card.Body>
              <h3>{order.eventId ? "Yes" : "No"}</h3>
              <p className="mb-0">Event Linked</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Card style={{
        border: "none",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        borderRadius: "12px"
      }}>
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            <Row>
              <Col md={3}>
                <Nav variant="pills" className="flex-column">
                  <Nav.Item>
                    <Nav.Link eventKey="overview">Overview</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="tickets">Tickets</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="user">User Details</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="event">Event Details</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="analytics">View Analytics</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col md={9}>
                <Tab.Content>
                  <Tab.Pane eventKey="overview">
                    <Card>
                      <Card.Body>
                        <h6>Order Information</h6>
                        <Table borderless>
                          <tbody>
                            <tr>
                              <td><strong>Order ID:</strong></td>
                              <td>{order.uniqueId}</td>
                            </tr>
                            <tr>
                              <td><strong>Event:</strong></td>
                              <td>{order.title || "N/A"}</td>
                            </tr>
                            <tr>
                              <td><strong>Amount:</strong></td>
                              <td>${order.amount || 0}</td>
                            </tr>
                            <tr>
                              <td><strong>Status:</strong></td>
                              <td>{getStatusBadge(order.status)}</td>
                            </tr>
                            <tr>
                              <td><strong>User Email:</strong></td>
                              <td>{order.userEmail || "N/A"}</td>
                            </tr>
                            <tr>
                              <td><strong>Event ID:</strong></td>
                              <td>{order.eventId || "N/A"}</td>
                            </tr>
                            <tr>
                              <td><strong>Order Date:</strong></td>
                              <td>{order.createdAt?.toLocaleString() || "N/A"}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </Tab.Pane>

                  <Tab.Pane eventKey="tickets">
                    <Card>
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">Tickets</h6>
                          <Badge bg="secondary">{orderTickets.length}</Badge>
                        </div>

                        {loadingTickets ? (
                          <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                            <div className="mt-2 text-muted">Loading tickets...</div>
                          </div>
                        ) : orderTickets.length > 0 ? (
                          <Table responsive hover>
                            <thead>
                              <tr>
                                <th>Ticket ID</th>
                                <th>Ticket Type</th>
                                <th>Seat</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Check-in</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orderTickets.map((ticket) => (
                                <tr key={ticket.id || `${ticket.ticketType}-${ticket.seat}`}>
                                  <td>
                                    <code>{ticket.id || "N/A"}</code>
                                  </td>
                                  <td>{getTicketTypeLabel(ticket.ticketType)}</td>
                                  <td>
                                    {ticket.seat ? (
                                      <Badge bg="info">{ticket.seat}</Badge>
                                    ) : (
                                      <span className="text-muted">No seat</span>
                                    )}
                                  </td>
                                  <td>{getTicketHolderName(ticket)}</td>
                                  <td>{ticket.email || order.userEmail || "N/A"}</td>
                                  <td>{ticket.status ? getStatusBadge(ticket.status) : <span className="text-muted">N/A</span>}</td>
                                  <td>
                                    <Badge bg={ticket.checkin ? "success" : "secondary"}>
                                      {ticket.checkin ? "Yes" : "No"}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        ) : order.cartItems?.length > 0 ? (
                          <>
                            <Alert variant="info">
                              No selected ticket records are attached to this order yet. Showing the order item summary.
                            </Alert>
                            <Table responsive hover>
                              <thead>
                                <tr>
                                  <th>Ticket Type</th>
                                  <th>Quantity</th>
                                  <th>Price</th>
                                </tr>
                              </thead>
                              <tbody>
                                {order.cartItems.map((item, index) => (
                                  <tr key={`${item.priceId || item.priceDescription || "item"}-${index}`}>
                                    <td>{item.priceDescription || item.name || "Ticket"}</td>
                                    <td>{item.quantity || 1}</td>
                                    <td>${item.price || 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </>
                        ) : (
                          <Alert variant="info" className="mb-0">
                            No selected tickets found for this order.
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab.Pane>

                  <Tab.Pane eventKey="user">
                    <Card>
                      <Card.Body>
                        {user ? (
                          <>
                            <h6>User Information</h6>
                            <Row>
                              <Col md={4} className="text-center">
                                {user.profileURL ? (
                                  <img
                                    src={user.profileURL}
                                    alt={(user.firstName || "") + " " + (user.lastName || "")}
                                    className="rounded-circle mb-3"
                                    width="120"
                                    height="120"
                                  />
                                ) : (
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center text-white mx-auto mb-3"
                                    style={{
                                      width: "120px",
                                      height: "120px",
                                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                      fontSize: "3rem"
                                    }}
                                  >
                                    {user.firstName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || "U"}
                                  </div>
                                )}
                                <h5>{(user.firstName || "") + " " + (user.lastName || "")}</h5>
                                <p className="text-muted">{user.email}</p>
                              </Col>
                              <Col md={8}>
                                <Table borderless>
                                  <tbody>
                                    <tr>
                                      <td><strong>User ID:</strong></td>
                                      <td>{user.id}</td>
                                    </tr>
                                    <tr>
                                      <td><strong>Phone:</strong></td>
                                      <td>{user.phone || "N/A"}</td>
                                    </tr>
                                    <tr>
                                      <td><strong>Organization:</strong></td>
                                      <td>{user.userOrg || "N/A"}</td>
                                    </tr>
                                    <tr>
                                      <td><strong>Created:</strong></td>
                                      <td>{user.createdAt?.toLocaleString() || "N/A"}</td>
                                    </tr>
                                    <tr>
                                      <td><strong>Last Login:</strong></td>
                                      <td>{user.lastLogin?.toLocaleString() || "Never"}</td>
                                    </tr>
                                  </tbody>
                                </Table>
                              </Col>
                            </Row>
                          </>
                        ) : (
                          <Alert variant="info">User information not available</Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab.Pane>

                  <Tab.Pane eventKey="event">
                    <Card>
                      <Card.Body>
                        {event ? (
                          <>
                            <h6>Event Information</h6>
                            <Table borderless>
                              <tbody>
                                <tr>
                                  <td><strong>Event ID:</strong></td>
                                  <td>{event.id}</td>
                                </tr>
                                <tr>
                                  <td><strong>Title:</strong></td>
                                  <td>{event.title || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Description:</strong></td>
                                  <td>{event.description || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Start Date:</strong></td>
                                  <td>{event.startDate?.toDate?.()?.toLocaleString() || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>End Date:</strong></td>
                                  <td>{event.endDate?.toDate?.()?.toLocaleString() || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Address:</strong></td>
                                  <td>{event.address?.fullAddress || "N/A"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Status:</strong></td>
                                  <td>{event.status || "N/A"}</td>
                                </tr>
                              </tbody>
                            </Table>
                          </>
                        ) : (
                          <Alert variant="info">Event information not available</Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab.Pane>

                  <Tab.Pane eventKey="analytics">
                    <Card>
                      <Card.Body>
                        <h6>View Analytics</h6>
                        {order.viewData ? (
                          <Table borderless>
                            <tbody>
                              <tr>
                                <td><strong>Location:</strong></td>
                                <td>
                                  {typeof order.viewData === 'object' && order.viewData 
                                    ? `${order.viewData.city || 'Unknown'}, ${order.viewData.region || 'Unknown'}, ${order.viewData.country || 'Unknown'}`
                                    : order.viewData || "Unknown"}
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Campaign ID:</strong></td>
                                <td>{typeof order.viewData === 'object' && order.viewData ? order.viewData.campaignId || "N/A" : "N/A"}</td>
                              </tr>
                              <tr>
                                <td><strong>Timezone:</strong></td>
                                <td>{typeof order.viewData === 'object' && order.viewData ? order.viewData.timezone || "N/A" : "N/A"}</td>
                              </tr>
                              <tr>
                                <td><strong>Device:</strong></td>
                                <td>
                                  {typeof order.viewData === 'object' && order.viewData && order.viewData.fromUA 
                                    ? parseUserAgent(order.viewData.fromUA)
                                    : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td><strong>Coordinates:</strong></td>
                                <td>
                                  {typeof order.viewData === 'object' && order.viewData 
                                    ? `${order.viewData.lat || 'N/A'}, ${order.viewData.lon || 'N/A'}`
                                    : "N/A"}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        ) : (
                          <Alert variant="info">View analytics not available</Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Card.Body>
      </Card>


    </Container>
  );
} 
